import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { Params } from '@angular/router';
import { ErrorObject } from 'src/app/_models/error';
import { ExperimentTemplate } from 'src/app/_models/experiment-template';
import { ErrorPageComponent } from 'src/app/_components/error-page/error-page.component';
import { AuthenticationService } from 'src/app/_services/authentication.service';
import { ExperimentMode } from 'src/app/_models/experiment-mode';
import { advancedNameRegex } from 'src/app/_helpers/utils';
import { stepValidator } from 'src/app/_helpers/step-validator.validator';
import { noWhitespaceValidator } from 'src/app/_helpers/no-whitespace.validator';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
  MatExpansionPanelDescription,
} from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { RadioButtonComponent } from 'src/app/_components/selection-controls/radio-button/radio-button.component';
import { UnderlinedFormFieldComponent } from 'src/app/_components/forms/underlined-form-field/underlined-form-field.component';
import { NumberFormFieldComponent } from 'src/app/_components/forms/number-form-field/number-form-field.component';
import { SlideToggleComponent } from 'src/app/_components/selection-controls/slide-toggle/slide-toggle.component';
import { UnderlinedDropdownFormFieldComponent } from 'src/app/_components/forms/underlined-dropdown-form-field/underlined-dropdown-form-field.component';
import { FluorescenceChannelCardComponent } from 'src/app/_components/cards/fluorescence-channel-card/fluorescence-channel-card.component';
import { Flask } from 'src/app/_helpers/experiment-utils';
import { ExperimentTemplateService } from 'src/app/_services/experiment-template.service';
import { User } from 'src/app/_models/user';
import { takeUntil } from 'rxjs';
import { ChangelogResponse } from 'src/app/_models/api-responses';
import { ValidationModalComponent } from 'src/app/_components/modals/validation-modal/validation-modal.component';
import { ModalComponent } from 'src/app/_components/modals/modal.component';
import { ButtonComponent } from 'src/app/_components/buttons/button/button.component';
import { MatOutlinedButtonComponent } from 'src/app/_components/buttons/mat-outlined-button/mat-outlined-button.component';
import { RoleCheckPipe } from '../../_helpers/role-check.pipe';
import { MatTableModule } from '@angular/material/table';
import { DatePipe } from '@angular/common';
import { IconButtonComponent } from 'src/app/_components/buttons/icon-button/icon-button.component';
import { DropdownFormFieldComponent } from 'src/app/_components/forms/dropdown-form-field/dropdown-form-field.component';
import { ChipRadioButtonComponent } from 'src/app/_components/selection-controls/chip-radio-button/chip-radio-button.component';
import { ChipCheckboxGroupComponent } from 'src/app/_components/selection-controls/chip-checkbox-group/chip-checkbox-group.component';
import { MatIconButtonComponent } from 'src/app/_components/buttons/mat-icon-button/mat-icon-button.component';
import { FormBase } from 'src/app/_models/form-base';
import { ExperimentFormBase } from '../_base/experiment-form.base';
import { LoadingSpinnerComponent } from 'src/app/_components/loading-spinner/loading-spinner.component';
import { UnderlinedTextareaFieldComponent } from 'src/app/_components/forms/underlined-textarea-field/underlined-textarea-field.component';
import { ExperimentParameterRanges } from '../../_helpers/experiment-min-max';
import { FluorescenceChannel } from 'src/app/_models/fluorescence-channel';

interface Option {
  key: string | null;
  value: string;
  options?: string[];
}

@Component({
  selector: 'app-template',
  imports: [
    ErrorPageComponent,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatExpansionPanelDescription,
    MatIcon,
    RadioButtonComponent,
    UnderlinedFormFieldComponent,
    NumberFormFieldComponent,
    SlideToggleComponent,
    UnderlinedDropdownFormFieldComponent,
    FluorescenceChannelCardComponent,
    ValidationModalComponent,
    ModalComponent,
    ButtonComponent,
    MatOutlinedButtonComponent,
    RoleCheckPipe,
    MatTableModule,
    DatePipe,
    IconButtonComponent,
    DropdownFormFieldComponent,
    ChipRadioButtonComponent,
    ChipCheckboxGroupComponent,
    MatIconButtonComponent,
    LoadingSpinnerComponent,
    UnderlinedTextareaFieldComponent,
  ],
  templateUrl: './template.component.html',
  styleUrl: './template.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateComponent
  extends ExperimentFormBase
  implements OnInit, OnDestroy
{
  // ── Abstract member implementations ──────────────────────────────────────────

  experimentTemplateId = signal<number | null>(null);
  override readonly entityId = this.experimentTemplateId;
  override fetchChangelog(): void {
    this.getExperimentTemplateChangelog();
  }
  override getSavedEntity(): ExperimentTemplate | null {
    return this.editExperimentTemplate() ?? this.newExperimentTemplate();
  }
  override updateIdInUrl(id: number): void {
    window.history.replaceState({}, '', `/experiment/templates/${id}/edit`);
  }

  // ── Component-specific services ───────────────────────────────────────────────

  private readonly authenticationService = inject(AuthenticationService);
  private readonly experimentTemplateService = inject(
    ExperimentTemplateService,
  );

  // Control vars
  getExperimentTemplateError = signal<ErrorObject | null>(null);
  error = signal<ErrorObject | null>(null);
  changelogError = signal<ErrorObject | null>(null);
  deleteTemplateError = signal<ErrorObject | null>(null);
  loadingTemplateData = signal<boolean>(false);
  deleteIsProcessing = signal<boolean>(false);
  pageInitialised = signal<boolean>(false);

  // Experiment data
  newExperimentTemplate = signal<ExperimentTemplate | null>(null);
  editExperimentTemplate = signal<ExperimentTemplate | null>(null);
  unsavedCachedData = signal<ExperimentTemplate | null>(null);
  isDuplicate = signal<boolean>(false);
  duplicateTemplateId = signal<number | null>(null);
  descriptionMinLength = ExperimentParameterRanges.DESCRIPTION.minLength;
  descriptionMaxLength = ExperimentParameterRanges.DESCRIPTION.maxLength;

  // Component-specific form fields

  experimentTemplateNameField = new FormBase<string>({
    key: 'templateName',
    label: 'Template Name',
    type: 'text',
    placeholder: 'Template name',
    disabled: false,
    required: true,
    value: '',
    options: [],
  });

  experimentDescriptionField = new FormBase<string>({
    key: 'description',
    label: 'Description',
    type: 'text',
    placeholder: 'Description',
    disabled: false,
    required: false,
    value: '',
    options: [],
  });

  override experimentControlsForm = this.formBuilder.group({
    // General experiment controls
    [this.experimentTemplateNameField.key]: [
      '',
      [Validators.minLength(2), Validators.maxLength(100), Validators.required],
    ],
    [this.experimentDescriptionField.key]: [
      '',
      [
        Validators.minLength(this.descriptionMinLength),
        Validators.maxLength(this.descriptionMaxLength),
        noWhitespaceValidator,
      ],
    ],
    [this.experimentNameField.key]: [
      '',
      [
        Validators.minLength(2),
        Validators.maxLength(100),
        Validators.required,
        Validators.pattern(advancedNameRegex),
      ],
    ],
    [this.organismField.key]: [
      '',
      [Validators.minLength(2), Validators.maxLength(100), Validators.required],
    ],
    [this.strainField.key]: [
      '',
      [Validators.minLength(2), Validators.maxLength(100), Validators.required],
    ],
    [this.mediaField.key]: [
      '',
      [Validators.minLength(2), Validators.maxLength(100), Validators.required],
    ],
    [this.samplingTimeField.key]: [
      this.samplingTimeField.value,
      Validators.compose([
        Validators.min(this.minSampleTimeInMins),
        Validators.max(this.maxSampleTimeInMins),
        Validators.required,
        stepValidator(this.sampleTimeStep),
      ]),
    ],
    [this.experimentTimeField.key]: [
      this.experimentTimeField.value,
      Validators.compose([
        Validators.min(this.minExperimentTimeInHours),
        Validators.max(this.maxExperimentTimeInHours),
        Validators.required,
        stepValidator(this.experimentTimeStep),
      ]),
    ],
    [this.speedAField.key]: [
      this.speedAField.value,
      Validators.compose([
        Validators.min(this.minSpeed),
        Validators.max(this.maxSpeed),
        stepValidator(this.speedStep),
      ]),
    ],
    [this.speedBField.key]: [
      this.speedBField.value,
      Validators.compose([
        Validators.min(this.minSpeed),
        Validators.max(this.maxSpeed),
        stepValidator(this.speedStep),
      ]),
    ],
    [this.speedCField.key]: [
      this.speedCField.value,
      Validators.compose([
        Validators.min(this.minSpeed),
        Validators.max(this.maxSpeed),
        stepValidator(this.speedStep),
      ]),
    ],
    [this.speedDField.key]: [
      this.speedDField.value,
      Validators.compose([
        Validators.min(this.minSpeed),
        Validators.max(this.maxSpeed),
        stepValidator(this.speedStep),
      ]),
    ],
    [this.setStirringSpeedAField.key]: [this.setStirringSpeedAField.value],
    [this.setStirringSpeedBField.key]: [this.setStirringSpeedBField.value],
    [this.setStirringSpeedCField.key]: [this.setStirringSpeedCField.value],
    [this.setStirringSpeedDField.key]: [this.setStirringSpeedDField.value],
    [this.ambientTemperatureField.key]: [
      this.ambientTemperatureField.value,
      [
        Validators.min(this.minAmbientTemp),
        Validators.max(this.maxAmbientTemp),
        stepValidator(this.ambientTempStep),
      ],
    ],
    [this.temperatureControlAField.key]: [this.temperatureControlAField.value],
    [this.temperatureControlBField.key]: [this.temperatureControlBField.value],
    [this.temperatureControlCField.key]: [this.temperatureControlCField.value],
    [this.temperatureControlDField.key]: [this.temperatureControlDField.value],
    [this.temperatureAField.key]: [
      this.temperatureAField.value,
      [
        Validators.min(this.minCultureTemp),
        Validators.max(this.maxAmbientTemp),
        stepValidator(this.cultureTempStep),
      ],
    ],
    [this.temperatureBField.key]: [
      this.temperatureBField.value,
      [
        Validators.min(this.minCultureTemp),
        Validators.max(this.maxAmbientTemp),
        stepValidator(this.cultureTempStep),
      ],
    ],
    [this.temperatureCField.key]: [
      this.temperatureCField.value,
      [
        Validators.min(this.minCultureTemp),
        Validators.max(this.maxAmbientTemp),
        stepValidator(this.cultureTempStep),
      ],
    ],
    [this.temperatureDField.key]: [
      this.temperatureDField.value,
      [
        Validators.min(this.minCultureTemp),
        Validators.max(this.maxAmbientTemp),
        stepValidator(this.cultureTempStep),
      ],
    ],
    [this.maintainODField.key]: [this.maintainODField.value],
    // Turbidostat controls
    [this.targetODAField.key]: [
      this.targetODAField.value,
      [
        Validators.min(this.minTargetOD),
        Validators.max(this.maxTargetOD),
        stepValidator(this.targetODStep),
      ],
    ],
    [this.targetODBField.key]: [
      this.targetODBField.value,
      [
        Validators.min(this.minTargetOD),
        Validators.max(this.maxTargetOD),
        stepValidator(this.targetODStep),
      ],
    ],
    [this.targetODCField.key]: [
      this.targetODCField.value,
      [
        Validators.min(this.minTargetOD),
        Validators.max(this.maxTargetOD),
        stepValidator(this.targetODStep),
      ],
    ],
    [this.targetODDField.key]: [
      this.targetODDField.value,
      [
        Validators.min(this.minTargetOD),
        Validators.max(this.maxTargetOD),
        stepValidator(this.targetODStep),
      ],
    ],
    [this.turbAActiveField.key]: [this.turbAActiveField.value],
    [this.turbBActiveField.key]: [this.turbBActiveField.value],
    [this.turbCActiveField.key]: [this.turbCActiveField.value],
    [this.turbDActiveField.key]: [this.turbDActiveField.value],
    // Chemostat controls
    [this.chemoAActiveField.key]: [this.chemoAActiveField.value],
    [this.chemoBActiveField.key]: [this.chemoBActiveField.value],
    [this.chemoCActiveField.key]: [this.chemoCActiveField.value],
    [this.chemoDActiveField.key]: [this.chemoDActiveField.value],
    [this.flowRateAField.key]: [
      this.flowRateAField.value,
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    [this.flowRateBField.key]: [
      this.flowRateBField.value,
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    [this.flowRateCField.key]: [
      this.flowRateCField.value,
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    [this.flowRateDField.key]: [
      this.flowRateDField.value,
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    // Morbidostat controls
    [this.morbidostatAActiveField.key]: [this.morbidostatAActiveField.value],
    [this.morbidostatBActiveField.key]: [this.morbidostatBActiveField.value],
    [this.morbidostatCActiveField.key]: [this.morbidostatCActiveField.value],
    [this.morbidostatDActiveField.key]: [this.morbidostatDActiveField.value],
    [this.thresholdODAField.key]: [
      this.thresholdODAField.value,
      Validators.compose([
        Validators.min(this.minThreshold),
        Validators.max(this.maxThreshold),
        stepValidator(this.thresholdStep),
      ]),
    ],
    [this.thresholdODBField.key]: [
      this.thresholdODBField.value,
      Validators.compose([
        Validators.min(this.minThreshold),
        Validators.max(this.maxThreshold),
        stepValidator(this.thresholdStep),
      ]),
    ],
    [this.thresholdODCField.key]: [
      this.thresholdODCField.value,
      Validators.compose([
        Validators.min(this.minThreshold),
        Validators.max(this.maxThreshold),
        stepValidator(this.thresholdStep),
      ]),
    ],
    [this.thresholdODDField.key]: [
      this.thresholdODDField.value,
      Validators.compose([
        Validators.min(this.minThreshold),
        Validators.max(this.maxThreshold),
        stepValidator(this.thresholdStep),
      ]),
    ],
    [this.dilutionPeriodAField.key]: [
      this.dilutionPeriodAField.value,
      Validators.compose([
        Validators.min(this.minDilutionPeriod),
        Validators.max(this.maxDilutionPeriod),
        stepValidator(this.dilutionPeriodStep),
      ]),
    ],
    [this.dilutionPeriodBField.key]: [
      this.dilutionPeriodBField.value,
      Validators.compose([
        Validators.min(this.minDilutionPeriod),
        Validators.max(this.maxDilutionPeriod),
        stepValidator(this.dilutionPeriodStep),
      ]),
    ],
    [this.dilutionPeriodCField.key]: [
      this.dilutionPeriodCField.value,
      Validators.compose([
        Validators.min(this.minDilutionPeriod),
        Validators.max(this.maxDilutionPeriod),
        stepValidator(this.dilutionPeriodStep),
      ]),
    ],
    [this.dilutionPeriodDField.key]: [
      this.dilutionPeriodDField.value,
      Validators.compose([
        Validators.min(this.minDilutionPeriod),
        Validators.max(this.maxDilutionPeriod),
        stepValidator(this.dilutionPeriodStep),
      ]),
    ],
    [this.dilutionFactorAField.key]: [
      this.dilutionFactorAField.value,
      Validators.compose([
        Validators.min(this.minDilutionFactor),
        Validators.max(this.maxDilutionFactor),
        stepValidator(this.dilutionFactorStep),
      ]),
    ],
    [this.dilutionFactorBField.key]: [
      this.dilutionFactorBField.value,
      Validators.compose([
        Validators.min(this.minDilutionFactor),
        Validators.max(this.maxDilutionFactor),
        stepValidator(this.dilutionFactorStep),
      ]),
    ],
    [this.dilutionFactorCField.key]: [
      this.dilutionFactorCField.value,
      Validators.compose([
        Validators.min(this.minDilutionFactor),
        Validators.max(this.maxDilutionFactor),
        stepValidator(this.dilutionFactorStep),
      ]),
    ],
    [this.dilutionFactorDField.key]: [
      this.dilutionFactorDField.value,
      Validators.compose([
        Validators.min(this.minDilutionFactor),
        Validators.max(this.maxDilutionFactor),
        stepValidator(this.dilutionFactorStep),
      ]),
    ],
    // pH controls
    [this.phControlAField.key]: [this.phControlAField.value],
    [this.phControlBField.key]: [this.phControlBField.value],
    [this.phControlCField.key]: [this.phControlCField.value],
    [this.phControlDField.key]: [this.phControlDField.value],
    [this.targetPHAField.key]: [
      this.targetPHAField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.targetPHBField.key]: [
      this.targetPHBField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.targetPHCField.key]: [
      this.targetPHCField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.targetPHDField.key]: [
      this.targetPHDField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.inputTypeAField.key]: [this.inputTypeAField.value],
    [this.inputTypeBField.key]: [this.inputTypeBField.value],
    [this.inputTypeCField.key]: [this.inputTypeCField.value],
    [this.inputTypeDField.key]: [this.inputTypeDField.value],
    [this.pumpDurationAField.key]: [
      this.pumpDurationAField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.pumpDurationBField.key]: [
      this.pumpDurationBField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.pumpDurationCField.key]: [
      this.pumpDurationCField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.pumpDurationDField.key]: [
      this.pumpDurationDField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    // pH 2x controls
    [this.ph2ControlAField.key]: [this.ph2ControlAField.value],
    [this.ph2ControlBField.key]: [this.ph2ControlBField.value],
    [this.targetPH2AField.key]: [
      this.targetPH2AField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.targetPH2BField.key]: [
      this.targetPH2BField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.pumpDuration2AField.key]: [
      this.pumpDuration2AField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.pumpDuration2BField.key]: [
      this.pumpDuration2BField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    // pH + Chemostat controls
    [this.phChemoControlAField.key]: [this.phChemoControlAField.value],
    [this.phChemoControlBField.key]: [this.phChemoControlBField.value],
    [this.targetPHChemoAField.key]: [
      this.targetPHChemoAField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.targetPHChemoBField.key]: [
      this.targetPHChemoBField.value,
      Validators.compose([
        Validators.min(this.minTargetPH),
        Validators.max(this.maxTargetPH),
        stepValidator(this.targetPHStep),
      ]),
    ],
    [this.pumpDurationChemoAField.key]: [
      this.pumpDurationChemoAField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.pumpDurationChemoBField.key]: [
      this.pumpDurationChemoBField.value,
      Validators.compose([
        Validators.min(this.minPumpDuration),
        Validators.max(this.maxPumpDuration),
        stepValidator(this.pumpDurationStep),
      ]),
    ],
    [this.inputTypeChemoAField.key]: [this.inputTypeChemoAField.value],
    [this.inputTypeChemoBField.key]: [this.inputTypeChemoBField.value],
    [this.flowRatePHAField.key]: [
      this.flowRatePHAField.value,
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    [this.flowRatePHBField.key]: [
      this.flowRatePHBField.value,
      Validators.compose([
        Validators.min(this.minFlowRate),
        Validators.max(this.maxFlowRate),
        stepValidator(this.flowRateStep),
      ]),
    ],
    // Fluoro controls
    [this.led1OnField.key]: [this.led1OnField.value],
    [this.led2OnField.key]: [this.led2OnField.value],
    [this.led3OnField.key]: [this.led3OnField.value],
    [this.led1IntensityField.key]: [
      this.led1IntensityField.value,
      Validators.compose([
        Validators.min(this.minLEDIntensity),
        Validators.max(this.maxLEDIntensity),
        stepValidator(this.ledIntensityStep),
      ]),
    ],
    [this.led2IntensityField.key]: [
      this.led2IntensityField.value,
      Validators.compose([
        Validators.min(this.minLEDIntensity),
        Validators.max(this.maxLEDIntensity),
        stepValidator(this.ledIntensityStep),
      ]),
    ],
    [this.led3IntensityField.key]: [
      this.led3IntensityField.value,
      Validators.compose([
        Validators.min(this.minLEDIntensity),
        Validators.max(this.maxLEDIntensity),
        stepValidator(this.ledIntensityStep),
      ]),
    ],
    [this.integrationTimeAField.key]: [
      this.integrationTimeAField.value,
      Validators.compose([
        Validators.min(this.minIntegrationTime),
        Validators.max(this.maxIntegrationTime),
        stepValidator(this.integrationTimeStep),
      ]),
    ],
    [this.integrationTimeBField.key]: [
      this.integrationTimeBField.value,
      Validators.compose([
        Validators.min(this.minIntegrationTime),
        Validators.max(this.maxIntegrationTime),
        stepValidator(this.integrationTimeStep),
      ]),
    ],
    [this.integrationTimeCField.key]: [
      this.integrationTimeCField.value,
      Validators.compose([
        Validators.min(this.minIntegrationTime),
        Validators.max(this.maxIntegrationTime),
        stepValidator(this.integrationTimeStep),
      ]),
    ],
    [this.integrationTimeDField.key]: [
      this.integrationTimeDField.value,
      Validators.compose([
        Validators.min(this.minIntegrationTime),
        Validators.max(this.maxIntegrationTime),
        stepValidator(this.integrationTimeStep),
      ]),
    ],
    [this.specGainAField.key]: [this.specGainAField.value],
    [this.specGainBField.key]: [this.specGainBField.value],
    [this.specGainCField.key]: [this.specGainCField.value],
    [this.specGainDField.key]: [this.specGainDField.value],
    [this.fluorescenceChannelsField.key]: [
      this.fluorescenceChannelsField.value,
    ],
    // Oxygen controls
    [this.salinityAField.key]: [
      this.salinityAField.value,
      Validators.compose([
        Validators.min(this.minSalinity),
        Validators.max(this.maxSalinity),
        stepValidator(this.salinityStep),
      ]),
    ],
    [this.salinityBField.key]: [
      this.salinityBField.value,
      Validators.compose([
        Validators.min(this.minSalinity),
        Validators.max(this.maxSalinity),
        stepValidator(this.salinityStep),
      ]),
    ],
    [this.salinityCField.key]: [
      this.salinityCField.value,
      Validators.compose([
        Validators.min(this.minSalinity),
        Validators.max(this.maxSalinity),
        stepValidator(this.salinityStep),
      ]),
    ],
    [this.salinityDField.key]: [
      this.salinityDField.value,
      Validators.compose([
        Validators.min(this.minSalinity),
        Validators.max(this.maxSalinity),
        stepValidator(this.salinityStep),
      ]),
    ],
    [this.spargingControlTypeAField.key]: [
      this.spargingControlTypeAField.value,
      [],
    ],
    [this.spargingControlTypeBField.key]: [
      this.spargingControlTypeBField.value,
      [],
    ],
    [this.spargingControlTypeCField.key]: [
      this.spargingControlTypeCField.value,
      [],
    ],
    [this.spargingControlTypeDField.key]: [
      this.spargingControlTypeDField.value,
      [],
    ],
    [this.experimentModeField.key]: [this.experimentModeField.value, []],
  });
  ngOnInit(): void {
    this.initialiseCheckboxes();

    this.authenticationService.currentUser$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((user: User) => {
        this.currentUser.set(user);
      });

    this.laboratory.set(this.authenticationService.selectedLaboratory);

    this.unsavedCachedData.set(
      this.experimentTemplateService.retrieveAndClearUnsavedCache(),
    );

    this.route.params
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((params: Params) => {
        this.experimentTemplateId.set(
          params.id != null ? Number(params.id) : null,
        );
      });

    if (window.history.state.data) {
      this.duplicateTemplateId.set(window.history.state.data);
      this.isDuplicate.set(true);
    }

    this.getFluorescenceChannels();

    // check route params for experimentTemplateId (and isDuplicate? might not be needed...just the experimenttemplateId ) if present, load experiment template and populate form

    if (
      this.experimentTemplateId() !== 0 &&
      this.experimentTemplateId() !== null
    ) {
      if (this.unsavedCachedData()) {
        // Set form with cached draft data
        this.setDraftCachedData();
      } else {
        // Fetch experiment data from API
        this.getExperimentTemplateFromApi(this.experimentTemplateId());
      }
    } else if (this.isDuplicate() && this.duplicateTemplateId() !== null) {
      // Duplicate flow — load the source template to pre-fill the form
      this.getExperimentTemplateFromApi(this.duplicateTemplateId());
    } else if (this.unsavedCachedData()) {
      // Set form with cached new experiment data
      this.setUnsavedCachedData();
    } else {
      this.initialiseSubscriptions();
      // Disable fluorescence fields initially since useFluorescence defaults to false
      this.setFluorescenceFieldsState(false);
      this.pageInitialised.set(true);
    }
  }

  setDraftCachedData(): void {
    this.editExperimentTemplate.set(this.unsavedCachedData());
    if (this.hasEditAccess()) {
      this.initialiseSubscriptions();
    }
    this.getExperimentTemplateChangelog();
    this.useFluorescence.set(this.unsavedCachedData().useFluorescence);
    this.useOxygen.set(this.unsavedCachedData().useOxygen);
    this.usePH.set(this.unsavedCachedData().usePH);
    this.useSparging.set(this.unsavedCachedData().useSparging);
    if (this.useFluorescence()) {
      this.selectedFluorescenceChannels.set(
        this.editExperimentTemplate().fluorescenceChannels,
      );
    }
    // Enable/disable fluorescence fields based on loaded experiment data
    this.setFluorescenceFieldsState(this.useFluorescence());
    this.initialiseCheckboxes();
    this.filterFluorescenceChannelOptions();
    this.updateFormValues(this.unsavedCachedData());
    this.pageInitialised.set(true);
  }

  setUnsavedCachedData(): void {
    this.newExperimentTemplate.set(this.unsavedCachedData());
    this.initialiseSubscriptions();
    this.useFluorescence.set(this.unsavedCachedData().useFluorescence);
    this.useOxygen.set(this.unsavedCachedData().useOxygen);
    this.usePH.set(this.unsavedCachedData().usePH);
    this.useSparging.set(this.unsavedCachedData().useSparging);
    // Enable/disable fluorescence fields based on loaded experiment data
    this.setFluorescenceFieldsState(this.useFluorescence());
    this.initialiseCheckboxes();
    if (this.useFluorescence()) {
      this.selectedFluorescenceChannels.set(
        this.fluorescenceChannels().filter((channel) =>
          this.newExperimentTemplate().fluorescenceChannels?.some(
            (fc) => fc.memorySlot === channel.memorySlot,
          ),
        ),
      );
    }
    this.filterFluorescenceChannelOptions();
    this.updateFormValues(this.unsavedCachedData(), false);
    this.pageInitialised.set(true);
  }

  initialiseSubscriptions(): void {
    // If maintainOD is true, target OD fields to be disabled
    this.experimentControlsForm
      .get(this.maintainODField.key)
      .valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((value) => {
        const targetODFields = ['A', 'B', 'C', 'D'] as const;
        targetODFields.forEach((flask) => {
          if (value === false) {
            // When maintainOD is false, only enable target OD if the individual control is active
            const turbActiveField = this[`turb${flask}ActiveField` as const];
            const isControlActive = this.f[turbActiveField.key]
              .value as boolean;
            this.setFlaskControlState(flask, 'turbidostat', isControlActive);
          } else {
            // When maintainOD is true, disable all target OD fields
            this.setFlaskControlState(flask, 'turbidostat', false);
          }
        });
      });

    // Stirring speed controls
    (['A', 'B', 'C', 'D'] as Flask[]).forEach((flask) => {
      const setSpeedField = this[`setStirringSpeed${flask}Field` as const];
      this.experimentControlsForm
        .get(setSpeedField.key)
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          this.updateStirringSpeedState(flask, value);
        });
    });

    // Temperature controls
    (['A', 'B', 'C', 'D'] as const).forEach((flask) => {
      const tempControlField = this[`temperatureControl${flask}Field` as const];
      this.experimentControlsForm
        .get(tempControlField.key)
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          this.setFlaskControlState(flask, 'temperature', value);
        });
    });

    // Turbidostat controls
    (['A', 'B', 'C', 'D'] as const).forEach((flask) => {
      const turbActiveField = this[`turb${flask}ActiveField` as const];
      this.experimentControlsForm
        .get(turbActiveField.key)
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          // Only enable target OD fields if maintainOD is false
          const maintainOD = this.f[this.maintainODField.key].value as boolean;
          const shouldEnable = value && !maintainOD;
          this.setFlaskControlState(flask, 'turbidostat', shouldEnable);
        });
    });

    // Chemostat controls
    (['A', 'B', 'C', 'D'] as const).forEach((flask) => {
      const chemoActiveField = this[`chemo${flask}ActiveField` as const];
      this.experimentControlsForm
        .get(chemoActiveField.key)
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          this.setFlaskControlState(flask, 'chemostat', value);
        });
    });

    // pH controls
    (['A', 'B', 'C', 'D'] as const).forEach((flask) => {
      const phControlField = this[`phControl${flask}Field` as const];
      this.experimentControlsForm
        .get(phControlField.key)
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          this.setFlaskControlState(flask, 'ph', value);
        });
    });

    // pH 2x controls
    (['A', 'B'] as const).forEach((flask) => {
      const ph2ControlField = this[`ph2Control${flask}Field` as const];
      this.experimentControlsForm
        .get(ph2ControlField.key)
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          this.setFlaskControlState(flask, 'ph2', value);
        });
    });

    // pH Chemo controls
    (['A', 'B'] as const).forEach((flask) => {
      const phChemoControlField = this[`phChemoControl${flask}Field` as const];
      this.experimentControlsForm
        .get(phChemoControlField.key)
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          this.setFlaskControlState(flask, 'phChemo', value);
        });
    });

    // Morbidostat controls
    (['A', 'B', 'C', 'D'] as const).forEach((flask) => {
      const morbidostatActiveField = this[`morbidostat${flask}ActiveField` as const];
      this.experimentControlsForm
        .get(morbidostatActiveField.key)!
        .valueChanges.pipe(takeUntil(this.unsubscribe$))
        .subscribe((value) => {
          this.setFlaskControlState(flask, 'morbidostat', value);
        });
    });

    // Fluoro channels
    this.experimentControlsForm
      .get(this.fluorescenceChannelsField.key)
      .valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((value) => {
        const channel = this.fluorescenceChannels().find(
          (option: FluorescenceChannel) => option.memorySlot === value,
        );
        if (channel && this.selectedFluorescenceChannels().length < 4) {
          this.selectedFluorescenceChannels.update((channels) => [
            ...channels,
            channel,
          ]);
          this.fluorescenceChannelsField.options =
            this.fluorescenceChannelsField.options.filter(
              (option: Option) => option.key !== value,
            );
          if (this.selectedFluorescenceChannels().length === 4) {
            this.experimentControlsForm
              .get(this.fluorescenceChannelsField.key)
              .disable();
          }
        }
      });
  }

  getExperimentTemplateFromApi(experimentTemplateId: number): void {
    this.getExperimentTemplateError.set(null);
    this.loadingTemplateData.set(true);

    this.experimentTemplateService
      .getExperimentTemplateById(experimentTemplateId)
      .subscribe({
        next: (response: ExperimentTemplate) => {
          this.editExperimentTemplate.set(response);
          if (this.hasEditAccess()) {
            this.initialiseSubscriptions();
          }
          // Handle fluoro selection
          if (response.useFluorescence) {
            this.selectedFluorescenceChannels.set(
              response.fluorescenceChannels,
            );
            this.filterFluorescenceChannelOptions();
          }
          if (!this.isDuplicate()) {
            this.getExperimentTemplateChangelog();
          }

          this.loadingTemplateData.set(false);
          this.useFluorescence.set(response.useFluorescence);
          this.useOxygen.set(response.useOxygen);
          this.usePH.set(response.usePH);
          this.useSparging.set(response.useSparging);
          // Enable/disable fluorescence fields based on loaded experiment data
          this.setFluorescenceFieldsState(this.useFluorescence());
          this.initialiseCheckboxes();
          this.updateFormValues(response);
          if (response.isDeleted) {
            this.experimentControlsForm.disable();
            this.moduleSelectForm.disable();
          }
          this.pageInitialised.set(true);
        },
        error: (error: ErrorObject) => {
          this.getExperimentTemplateError.set(error);
          this.loadingTemplateData.set(false);
          this.pageInitialised.set(true);
        },
      });
  }

  updateFormValues(
    experimentData: ExperimentTemplate,
    isEditTemplateExperiment: boolean = true,
  ): void {
    // Combine all form values using helper methods
    const formValues = {
      ...this.getBasicFormValues(experimentData),
      ...this.getDefaultModuleFormValues(experimentData),
      ...this.getTurbidostatFormValues(experimentData),
      ...this.getChemostatFormValues(experimentData),
      ...this.getMorbidostatFormValues(experimentData),
      ...this.getPHControlFormValues(experimentData),
      ...this.getFluorescenceFormValues(experimentData),
      ...this.getOxygenFormValues(experimentData),
      ...this.getSpargingFormValues(experimentData),
    };

    // Unsaved experiment - remove selected fluorescence channels from available options
    if (!isEditTemplateExperiment && experimentData.useFluorescence) {
      this.selectedFluorescenceChannels().forEach((channel) => {
        this.fluorescenceChannelsField.options =
          this.fluorescenceChannelsField.options.filter(
            (option: Option) => option.key !== channel.memorySlot.toString(),
          );
      });
    }

    // Set form values for fields that are not directly mapped to the experiment template properties
    this.experimentControlsForm.controls[
      this.experimentTemplateNameField.key
    ].setValue(experimentData.templateName);
    this.experimentControlsForm.controls[
      this.experimentDescriptionField.key
    ].setValue(experimentData.description);

    this.experimentControlsForm.patchValue(formValues);
    this.setModuleCheckboxes(isEditTemplateExperiment);

    if (isEditTemplateExperiment) {
      if (!this.isDuplicate()) {
        this.experimentControlsForm.controls[
          this.experimentModeField.key
        ].disable();
      }
      this.experimentControlsForm.markAsPristine();
    } else {
      this.experimentControlsForm.markAsDirty();
    }

    if (!this.hasEditAccess()) {
      this.experimentControlsForm.disable();
      this.moduleSelectForm.disable();
    }
  }

  setModuleCheckboxes(isEditTemplateExperiment: boolean): void {
    const oxygenControlIndex = this.moduleCheckboxesField.options.findIndex(
      (option: Option) => option.key === 'Oxygen',
    );

    if (oxygenControlIndex > -1) {
      this.checkboxesFormArray.controls[oxygenControlIndex].setValue(
        isEditTemplateExperiment
          ? this.editExperimentTemplate().useOxygen
          : this.newExperimentTemplate().useOxygen,
      );
    }

    const pHControlIndex = this.moduleCheckboxesField.options.findIndex(
      (option: Option) => option.key === 'pH',
    );

    if (pHControlIndex > -1) {
      this.checkboxesFormArray.controls[pHControlIndex].setValue(
        isEditTemplateExperiment
          ? this.editExperimentTemplate().usePH
          : this.newExperimentTemplate().usePH,
      );
    }

    const fluorescenceControlIndex =
      this.moduleCheckboxesField.options.findIndex(
        (option: Option) => option.key === 'Fluorescence',
      );

    if (fluorescenceControlIndex > -1) {
      this.checkboxesFormArray.controls[fluorescenceControlIndex].setValue(
        isEditTemplateExperiment
          ? this.editExperimentTemplate().useFluorescence
          : this.newExperimentTemplate().useFluorescence,
      );
    }

    const spargingControlIndex = this.moduleCheckboxesField.options.findIndex(
      (option: Option) => option.key === 'Sparging',
    );
    if (spargingControlIndex > -1) {
      this.checkboxesFormArray.controls[spargingControlIndex].setValue(
        isEditTemplateExperiment
          ? this.editExperimentTemplate().useSparging
          : this.newExperimentTemplate().useSparging,
      );
    }

    if (isEditTemplateExperiment) {
      this.moduleSelectForm.markAsPristine();
    } else {
      this.moduleSelectForm.markAsDirty();
    }
  }

  modalButtonClicked(buttonId: string): void {
    switch (buttonId) {
      case 'use-template-button':
        this.router.navigate(['/experiment/0/set-parameters'], {
          state: { templateId: this.experimentTemplateId() },
        });
        break;
      case 'close-create-button':
        this.closeModal('create-experiment-template');
        this.router.navigate([
          `experiment/templates/${this.experimentTemplateId()}/edit`,
        ]);
        break;
      case 'close-error-button':
        this.closeModal('error');
        break;
      case 'close-update-button':
        this.closeModal('update-experiment-template');
        break;
      case 'close-device-warning-button':
        this.closeModal('device-warning');
        break;
      case 'cancel-button':
        this.allowNavigation.next(false);
        this.closeModal('attention');
        break;
      case 'confirm-navigation':
        this.allowNavigation.next(true);
        this.closeModal('attention');
        break;
      case 'delete-success-button':
        this.closeModal('delete-success');
        this.allowNavigation.next(true);
        void this.router.navigate(['experiment/templates']);
        break;
      case 'error-primary-button':
        this.allowNavigation.next(false);
        this.deleteTemplateError.set(null);
        this.closeModal('delete-error');
        break;
    }
  }

  handleSubmit(): void {
    this.experimentControlsForm.markAllAsTouched();
    if (this.experimentControlsForm.invalid) return;

    if (this.experimentTemplateId()) {
      this.updateExperimentTemplate();
    } else {
      this.createExperimentTemplate();
    }
  }

  createExperimentTemplate(): void {
    // If form is already being submitted, exit
    if (this.isProcessing()) {
      return;
    }

    this.isProcessing.set(true);
    this.error.set(null);

    const formData = this.getCreateExperimentTemplateFormValues();
    this.newExperimentTemplate.set(formData);

    this.experimentTemplateService
      .createExperimentTemplate(this.newExperimentTemplate())
      .subscribe({
        next: (response: ExperimentTemplate) => {
          this.isProcessing.set(false);
          this.editExperimentTemplate.set(response);
          this.experimentTemplateId.set(response.id);
          this.updateIdInUrl(response.id);
          this.getExperimentTemplateChangelog();
          this.updateFormValues(this.editExperimentTemplate());
          this.experimentControlsForm.markAsPristine(); // TODO this should be handled by the form service
          this.openModal('create-experiment-template');
        },
        error: (error: ErrorObject) => {
          this.error.set(error);
          this.isProcessing.set(false);
          this.openModal('error');
        },
      });
  }

  updateExperimentTemplate(): void {
    if (this.isProcessing()) {
      return;
    }

    this.isProcessing.set(true);
    this.error.set(null);

    const formData = this.getEditExperimentTemplateFormValues();
    this.editExperimentTemplate.set(formData);

    this.experimentTemplateService
      .updateExperimentTemplate(this.editExperimentTemplate())
      .subscribe({
        next: (response: ExperimentTemplate) => {
          this.isProcessing.set(false);
          this.editExperimentTemplate.set(response);
          this.experimentTemplateId.set(response.id);
          this.getExperimentTemplateChangelog();
          this.updateFormValues(this.editExperimentTemplate());
          this.experimentControlsForm.markAsPristine();
          this.openModal('update-experiment-template');
        },
        error: (error: ErrorObject) => {
          this.error.set(error);
          this.isProcessing.set(false);
          this.openModal('error');
        },
      });
  }
  deleteExperimentTemplate(): void {
    // If request has already been submitted, exit
    if (this.isProcessing() || this.deleteIsProcessing()) {
      return;
    }

    this.deleteTemplateError.set(null);
    this.deleteIsProcessing.set(true);

    this.experimentTemplateService
      .deleteTemplate(this.experimentTemplateId())
      .subscribe({
        next: () => {
          this.deleteIsProcessing.set(false);
          this.closeModal('delete-template');
          this.openModal('delete-success');
        },
        error: (error: ErrorObject) => {
          this.deleteIsProcessing.set(false);
          this.deleteTemplateError.set(error);
          this.closeModal('delete-template');
          this.openModal('delete-error');
        },
      });
  }

  getExperimentTemplateChangelog(): void {
    if (!this.experimentTemplateId()) {
      return;
    }
    this.changelogError.set(null);
    this.experimentTemplateService
      .getExperimentTemplateChangelog(
        this.experimentTemplateId(),
        this.currentSort().sortBy,
        this.currentSort().sortDirection,
        this.pageSize(),
        this.pageRequested(),
      )
      .subscribe({
        next: (response: ChangelogResponse) => {
          this.changelogList.set(response.changelogList);
          if (response.paginationData) {
            this.currentPage.set(response.paginationData.CurrentPage);
            this.pageSize.set(response.paginationData.PageSize);
            this.totalPages.set(response.paginationData.TotalPages);
            this.hasNext.set(response.paginationData.HasNext);
            this.hasPrevious.set(response.paginationData.HasPrevious);
          }
        },
        error: (error: ErrorObject) => {
          this.changelogError.set(error);
        },
      });
  }

  cancelDuplicate(): void {
    this.router.navigate([`experiment/templates`]);
  }

  private getCreateExperimentTemplateFormValues(): ExperimentTemplate {
    const turbidostatValues = this.getTurbidostatValues();
    const chemostatValues = this.getChemostatValues();
    const morbidostatValues = this.getMorbidostatValues();
    const phControlValues = this.getPHControlValues();
    const fluorescenceValues = this.getFluorescenceValues();
    const oxygenValues = this.getOxygenValues();
    const spargingValues = this.getSpargingValues();

    return {
      laboratoryId: this.laboratory().laboratoryId,
      templateName: this.f[this.experimentTemplateNameField.key]
        .value as string,
      name: this.f[this.experimentNameField.key].value as string,
      organism: this.f[this.organismField.key].value as string,
      strain: this.f[this.strainField.key].value as string,
      media: this.f[this.mediaField.key].value as string,
      ambientTemperature: this.f[this.ambientTemperatureField.key]
        .value as number,
      samplingInterval: this.f[this.samplingTimeField.key].value as number,
      duration: this.f[this.experimentTimeField.key].value as number,
      speedRPMA: this.f[this.speedAField.key].value as number,
      speedRPMB: this.f[this.speedBField.key].value as number,
      speedRPMC: this.f[this.speedCField.key].value as number,
      speedRPMD: this.f[this.speedDField.key].value as number,
      temperatureControlA: this.f[this.temperatureControlAField.key]
        .value as boolean,
      temperatureControlB: this.f[this.temperatureControlBField.key]
        .value as boolean,
      temperatureControlC: this.f[this.temperatureControlCField.key]
        .value as boolean,
      temperatureControlD: this.f[this.temperatureControlDField.key]
        .value as boolean,
      cultureTemperatureA:
        this.f[this.temperatureControlAField.key].value === true
          ? (this.f[this.temperatureAField.key].value as number)
          : undefined,
      cultureTemperatureB:
        this.f[this.temperatureControlBField.key].value === true
          ? (this.f[this.temperatureBField.key].value as number)
          : undefined,
      cultureTemperatureC:
        this.f[this.temperatureControlCField.key].value === true
          ? (this.f[this.temperatureCField.key].value as number)
          : undefined,
      cultureTemperatureD:
        this.f[this.temperatureControlDField.key].value === true
          ? (this.f[this.temperatureDField.key].value as number)
          : undefined,
      useFluorescence: this.useFluorescence(),
      useOxygen: this.useOxygen(),
      usePH: this.usePH(),
      useSparging: this.useSparging(),
      experimentMode: this.f[this.experimentModeField.key]
        .value as ExperimentMode,
      description: (
        this.f[this.experimentDescriptionField.key].value as string
      ).trim(),
      ...turbidostatValues,
      ...chemostatValues,
      ...morbidostatValues,
      ...phControlValues,
      ...fluorescenceValues,
      ...oxygenValues,
      ...spargingValues,
    };
  }

  // TODO: might be able to get rid of this method and just use getCreateExperimentTemplateFormValues for both create and edit, since we're now preserving edit-specific fields in the form values
  private getEditExperimentTemplateFormValues(): ExperimentTemplate {
    const turbidostatValues = this.getTurbidostatValues();
    const chemostatValues = this.getChemostatValues();
    const morbidostatValues = this.getMorbidostatValues();
    const phControlValues = this.getPHControlValues();
    const fluorescenceValues = this.getFluorescenceValues();
    const oxygenValues = this.getOxygenValues();
    const spargingValues = this.getSpargingValues();

    return {
      id: this.experimentTemplateId(),
      laboratoryId: this.laboratory().laboratoryId,
      templateName: this.f[this.experimentTemplateNameField.key]
        .value as string,
      name: this.f[this.experimentNameField.key].value as string,
      organism: this.f[this.organismField.key].value as string,
      strain: this.f[this.strainField.key].value as string,
      media: this.f[this.mediaField.key].value as string,
      ambientTemperature: this.f[this.ambientTemperatureField.key]
        .value as number,
      samplingInterval: this.f[this.samplingTimeField.key].value as number,
      duration: this.f[this.experimentTimeField.key].value as number,
      speedRPMA: this.f[this.speedAField.key].value as number,
      speedRPMB: this.f[this.speedBField.key].value as number,
      speedRPMC: this.f[this.speedCField.key].value as number,
      speedRPMD: this.f[this.speedDField.key].value as number,
      temperatureControlA: this.f[this.temperatureControlAField.key]
        .value as boolean,
      temperatureControlB: this.f[this.temperatureControlBField.key]
        .value as boolean,
      temperatureControlC: this.f[this.temperatureControlCField.key]
        .value as boolean,
      temperatureControlD: this.f[this.temperatureControlDField.key]
        .value as boolean,
      cultureTemperatureA:
        this.f[this.temperatureControlAField.key].value === true
          ? (this.f[this.temperatureAField.key].value as number)
          : undefined,
      cultureTemperatureB:
        this.f[this.temperatureControlBField.key].value === true
          ? (this.f[this.temperatureBField.key].value as number)
          : undefined,
      cultureTemperatureC:
        this.f[this.temperatureControlCField.key].value === true
          ? (this.f[this.temperatureCField.key].value as number)
          : undefined,
      cultureTemperatureD:
        this.f[this.temperatureControlDField.key].value === true
          ? (this.f[this.temperatureDField.key].value as number)
          : undefined,
      useFluorescence: this.useFluorescence(),
      useOxygen: this.useOxygen(),
      usePH: this.usePH(),
      useSparging: this.useSparging(),
      experimentMode: this.f[this.experimentModeField.key]
        .value as ExperimentMode,
      description: (
        this.f[this.experimentDescriptionField.key].value as string
      ).trim(),
      ...turbidostatValues,
      ...chemostatValues,
      ...morbidostatValues,
      ...phControlValues,
      ...fluorescenceValues,
      ...oxygenValues,
      ...spargingValues,
    };
  }

  canExit(): Promise<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  ngOnDestroy(): void {
    if (
      this.authEventService.isUnauthorizedNavigationPending &&
      !this.experimentControlsForm.pristine
    ) {
      let formValues: ExperimentTemplate;
      if (this.experimentTemplateId() === null || this.isDuplicate()) {
        formValues = this.getCreateExperimentTemplateFormValues();
      } else {
        formValues = this.getEditExperimentTemplateFormValues();
      }
      this.experimentTemplateService.cacheUnsavedParameters(formValues);
    }

    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
